import React, { useState, useEffect, useCallback, useRef } from 'react';

function __computeApiBase() {
  const loc = window.location;
  const pathParts = loc.pathname.split('/');
  let idx = pathParts.indexOf('selenny-designer');
  if (idx < 0) idx = pathParts.indexOf('designer');
  if (idx >= 0) return loc.origin + pathParts.slice(0, idx + 1).join('/') + '/backend/api';
  return process.env.REACT_APP_API_URL || '/designer/api';
}
const API_BASE = __computeApiBase();

/**
 * MyDesignsPanel — "Meine Designs" gallery.
 * Fetches saved designs for the current customer and provides
 * load, duplicate, rename, delete and search functionality.
 *
 * Props:
 *  - customerEmail  : string (required for listing designs)
 *  - canvasManager  : ref to the CanvasManager instance
 *  - designId       : currently loaded design ID (for "active" indicator)
 *  - onDesignLoad   : callback(designData) — called when user wants to load a design
 *  - onClose        : callback to close the panel
 *  - inline         : boolean — if true, renders without the fixed-position wrapper (for mobile sheets)
 */
export default function MyDesignsPanel({ customerEmail, canvasManager, designId, onDesignLoad, onClose, inline = false }) {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date' | 'name'
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmLoadId, setConfirmLoadId] = useState(null);
  const renameInputRef = useRef(null);

  // Fetch designs from API
  const fetchDesigns = useCallback(async () => {
    if (!customerEmail) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/designs?email=${encodeURIComponent(customerEmail)}`);
      if (!res.ok) throw new Error('Fehler beim Laden');
      const data = await res.json();
      setDesigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('MyDesigns fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customerEmail]);

  useEffect(() => {
    fetchDesigns();
  }, [fetchDesigns]);

  // Focus rename input when entering rename mode
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  // Filter + sort
  const filteredDesigns = designs
    .filter(d => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (d.name || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      // date — newest first
      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    });

  // Delete
  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/designs/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Löschen fehlgeschlagen');
      }
      setDesigns(prev => prev.filter(d => d.id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      alert('Fehler: ' + err.message);
    }
  };

  // Duplicate
  const handleDuplicate = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/designs/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Duplizieren fehlgeschlagen');
      // Refresh list
      await fetchDesigns();
    } catch (err) {
      alert('Fehler: ' + err.message);
    }
  };

  // Rename
  const handleRename = async (id) => {
    const newName = renameValue.trim();
    if (!newName) { setRenamingId(null); return; }
    try {
      const res = await fetch(`${API_BASE}/designs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      if (!res.ok) throw new Error('Umbenennen fehlgeschlagen');
      setDesigns(prev => prev.map(d => d.id === id ? { ...d, name: newName } : d));
      setRenamingId(null);
    } catch (err) {
      alert('Fehler: ' + err.message);
    }
  };

  // Load design — navigate to it
  const handleLoad = (design) => {
    // Check if canvas has unsaved changes
    const hasObjects = canvasManager?.canvas?.getObjects()?.length > 0;
    if (hasObjects && design.id !== designId) {
      setConfirmLoadId(design.id);
      return;
    }
    doLoad(design.id);
  };

  const doLoad = (id) => {
    setConfirmLoadId(null);
    if (onDesignLoad) {
      onDesignLoad(id);
    } else {
      // Fallback: update URL and reload
      const url = new URL(window.location.href);
      url.searchParams.set('design_id', id);
      window.location.href = url.toString();
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch { return dateStr; }
  };

  // Preview URL builder
  const previewUrl = (id) => `${API_BASE}/designs/${id}/preview`;

  // Keyframe styles (injected once)
  const keyframes = (
    <style>{`
      @keyframes mydesigns-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      @keyframes mydesigns-fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
    `}</style>
  );

  // No email — show info
  if (!customerEmail) {
    const infoContent = (
      <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 12px', opacity: 0.4 }}>
          <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
          <path d="M12 16v-4M12 8h.01"/>
        </svg>
        <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Kein Kundenkonto erkannt</p>
        <p style={{ fontSize: 12 }}>Um deine gespeicherten Designs zu sehen, öffne den Designer über den Shop mit deiner E-Mail-Adresse.</p>
      </div>
    );
    if (inline) return infoContent;
    return (
      <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 animate-slideInLeft"
        style={{ left: 84, top: 80, width: 380, maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
        <PanelHeader onClose={onClose} />
        {infoContent}
      </div>
    );
  }

  const content = (
    <>
      {keyframes}
      {/* Search + Sort */}
      <div style={{ padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--selenny-border, #e5e7eb)' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }}>
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Design suchen..."
            className="w-full text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-1 focus:ring-blue-400"
            style={{ paddingLeft: 28, paddingRight: 8, paddingTop: 6, paddingBottom: 6, fontSize: 12, outline: 'none' }}
          />
        </div>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100"
          style={{ padding: '4px 6px', fontSize: 11, outline: 'none' }}
        >
          <option value="date">Neueste zuerst</option>
          <option value="name">Name A-Z</option>
        </select>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
            <div style={{
              width: 32, height: 32, margin: '0 auto 8px',
              border: '3px solid #e5e7eb', borderTopColor: '#3b82f6',
              borderRadius: '50%', animation: 'mydesigns-spin 0.8s linear infinite'
            }} />
            <span style={{ fontSize: 12 }}>Designs werden geladen...</span>
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: 24, color: '#ef4444' }}>
            <p style={{ fontSize: 13, marginBottom: 8 }}>{error}</p>
            <button onClick={fetchDesigns} style={{
              padding: '6px 16px', fontSize: 12, background: '#3b82f6', color: '#fff',
              border: 'none', borderRadius: 8, cursor: 'pointer'
            }}>Erneut versuchen</button>
          </div>
        )}

        {!loading && !error && filteredDesigns.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 8px', opacity: 0.3 }}>
              <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
            </svg>
            <p style={{ fontSize: 13, fontWeight: 500 }}>
              {search ? 'Keine Designs gefunden' : 'Noch keine Designs gespeichert'}
            </p>
            {!search && <p style={{ fontSize: 11, marginTop: 4 }}>Erstelle ein Design und speichere es, um es hier zu sehen.</p>}
          </div>
        )}

        {/* Design Grid */}
        {!loading && filteredDesigns.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {filteredDesigns.map(design => (
              <DesignCard
                key={design.id}
                design={design}
                isActive={design.id === designId}
                previewUrl={previewUrl(design.id)}
                onLoad={() => handleLoad(design)}
                onDuplicate={() => handleDuplicate(design.id)}
                onDelete={() => setConfirmDeleteId(design.id)}
                onRename={() => { setRenamingId(design.id); setRenameValue(design.name || ''); }}
                isRenaming={renamingId === design.id}
                renameValue={renameValue}
                onRenameChange={setRenameValue}
                onRenameSubmit={() => handleRename(design.id)}
                onRenameCancel={() => setRenamingId(null)}
                renameInputRef={renamingId === design.id ? renameInputRef : null}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 12
        }}>
          <div className="bg-white dark:bg-[#2c2c2e]" style={{ padding: 24, borderRadius: 16, maxWidth: 280, textAlign: 'center' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" style={{ margin: '0 auto 12px' }}>
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
              <path d="M15 9l-6 6M9 9l6 6"/>
            </svg>
            <p className="text-gray-800 dark:text-gray-100" style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Design löschen?</p>
            <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: 12, marginBottom: 16 }}>
              Dieses Design wird unwiderruflich gelöscht.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setConfirmDeleteId(null)} style={{
                flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600,
                background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer'
              }}>Abbrechen</button>
              <button onClick={() => handleDelete(confirmDeleteId)} style={{
                flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600,
                background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer'
              }}>Löschen</button>
            </div>
          </div>
        </div>
      )}

      {/* Load Confirmation Modal */}
      {confirmLoadId && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 12
        }}>
          <div className="bg-white dark:bg-[#2c2c2e]" style={{ padding: 24, borderRadius: 16, maxWidth: 300, textAlign: 'center' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5" style={{ margin: '0 auto 12px' }}>
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
            <p className="text-gray-800 dark:text-gray-100" style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Design öffnen?</p>
            <p className="text-gray-500 dark:text-gray-400" style={{ fontSize: 12, marginBottom: 16 }}>
              Nicht gespeicherte Änderungen am aktuellen Design gehen verloren.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setConfirmLoadId(null)} style={{
                flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600,
                background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer'
              }}>Abbrechen</button>
              <button onClick={() => doLoad(confirmLoadId)} style={{
                flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600,
                background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer'
              }}>Öffnen</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Inline mode (for mobile sheets)
  if (inline) return content;

  // Desktop: fixed panel next to toolbar
  return (
    <div className="fixed z-[100] bg-white dark:bg-[#2c2c2e] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 animate-slideInLeft"
      style={{ left: 84, top: 80, width: 380, maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PanelHeader onClose={onClose} onRefresh={fetchDesigns} count={filteredDesigns.length} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
        {content}
      </div>
    </div>
  );
}

// Panel header with title, count and close button
function PanelHeader({ onClose, onRefresh, count }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 12px', borderBottom: '1px solid var(--selenny-border, #e5e7eb)', flexShrink: 0
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-500">
          <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
        </svg>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Meine Designs</span>
        {count != null && <span className="text-[10px] bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-medium">{count}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {onRefresh && (
          <button onClick={onRefresh} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'none', cursor: 'pointer' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6M3 12a9 9 0 0115.4-6.4L21 8M3 22v-6h6M21 12a9 9 0 01-15.4 6.4L3 16"/></svg>
          </button>
        )}
        <button onClick={onClose} className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-0.5 rounded" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>Schliessen</button>
        <button onClick={onClose} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-lg leading-none" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>&times;</button>
      </div>
    </div>
  );
}

// Individual design card in the grid
function DesignCard({
  design, isActive, previewUrl, onLoad, onDuplicate, onDelete, onRename,
  isRenaming, renameValue, onRenameChange, onRenameSubmit, onRenameCancel,
  renameInputRef, formatDate
}) {
  const [imgError, setImgError] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [hovered, setHovered] = useState(false);

  const dimensions = `${Math.round(design.width_cm || 0)} × ${Math.round(design.height_cm || 0)} cm`;
  const statusColor = {
    draft: '#f59e0b',
    submitted: '#3b82f6',
    completed: '#10b981',
    in_production: '#8b5cf6',
  }[design.status] || '#9ca3af';

  return (
    <div
      style={{
        borderRadius: 12,
        border: isActive ? '2px solid #3b82f6' : '1px solid var(--selenny-border, #e5e7eb)',
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.15s ease',
        cursor: 'pointer',
      }}
      className="bg-white dark:bg-[#1c1c1e]"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => !isRenaming && !showMenu && onLoad()}
    >
      {/* Preview Image */}
      <div style={{
        height: 100, overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(135deg, #f9fafb, #f3f4f6)',
      }}
        className="dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900"
      >
        {!imgError ? (
          <img
            src={previewUrl}
            alt={design.name}
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            loading="lazy"
          />
        ) : (
          <div style={{
            width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', color: '#9ca3af'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4 }}>
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
            </svg>
            <span style={{ fontSize: 9, marginTop: 2 }}>Keine Vorschau</span>
          </div>
        )}

        {/* Active Badge */}
        {isActive && (
          <div style={{
            position: 'absolute', top: 6, left: 6, background: '#3b82f6', color: '#fff',
            fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 6,
          }}>AKTIV</div>
        )}

        {/* Status dot */}
        <div style={{
          position: 'absolute', top: 6, right: 6, width: 8, height: 8,
          borderRadius: '50%', background: statusColor,
          border: '1.5px solid rgba(255,255,255,0.8)',
        }} title={`Status: ${design.status || 'draft'}`} />

        {/* Expiration warning */}
        {design.expires_in_days != null && design.expires_in_days <= 30 && (
          <div style={{
            position: 'absolute', bottom: 4, left: 4, right: 4,
            background: 'rgba(239,68,68,0.9)', color: '#fff',
            fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4, textAlign: 'center',
          }}>
            Wird in {design.expires_in_days} Tagen gelöscht
          </div>
        )}

        {/* Context menu trigger */}
        <button
          onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
          style={{
            position: 'absolute', top: 4, right: (hovered || showMenu) ? 4 : -40, transition: 'right 0.2s',
            width: 24, height: 24, borderRadius: 6, border: 'none',
            background: 'rgba(0,0,0,0.5)', color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
          }}
        >
          &#8942;
        </button>
      </div>

      {/* Info Area */}
      <div style={{ padding: '8px 10px' }}>
        {isRenaming ? (
          <form onSubmit={(e) => { e.preventDefault(); onRenameSubmit(); }} style={{ display: 'flex', gap: 4 }}>
            <input
              ref={renameInputRef}
              type="text"
              value={renameValue}
              onChange={e => onRenameChange(e.target.value)}
              onBlur={onRenameSubmit}
              onKeyDown={e => { if (e.key === 'Escape') onRenameCancel(); }}
              onClick={e => e.stopPropagation()}
              className="text-xs rounded border border-blue-400 bg-blue-50 dark:bg-blue-900/30 dark:text-gray-100 text-gray-800"
              style={{ flex: 1, padding: '2px 6px', fontSize: 11, outline: 'none', minWidth: 0 }}
            />
          </form>
        ) : (
          <p
            className="text-xs font-medium text-gray-800 dark:text-gray-100"
            style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              lineHeight: '16px', cursor: 'text',
            }}
            onClick={(e) => { e.stopPropagation(); onRename(); }}
            title="Zum Umbenennen klicken"
          >
            {design.name || 'Unbenanntes Design'}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 3 }}>
          <span className="text-gray-400 dark:text-gray-500" style={{ fontSize: 10 }}>{dimensions}</span>
          <span className="text-gray-400 dark:text-gray-500" style={{ fontSize: 9 }}>{formatDate(design.updated_at || design.created_at)}</span>
        </div>
      </div>

      {/* Context Menu Dropdown */}
      {showMenu && (
        <div
          onClick={e => e.stopPropagation()}
          className="bg-white dark:bg-[#2c2c2e] border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl"
          style={{
            position: 'absolute', top: 32, right: 8, zIndex: 5, minWidth: 140,
            overflow: 'hidden', animation: 'mydesigns-fadeIn 0.15s ease',
          }}
        >
          <MenuButton icon={openIcon} label="Öffnen" onClick={() => { setShowMenu(false); onLoad(); }} />
          <MenuButton icon={copyIcon} label="Kopieren" onClick={() => { setShowMenu(false); onDuplicate(); }} />
          <MenuButton icon={renameIcon} label="Umbenennen" onClick={() => { setShowMenu(false); onRename(); }} />
          <div style={{ height: 1, background: 'var(--selenny-border, #e5e7eb)', margin: '2px 0' }} />
          <MenuButton icon={deleteIcon} label="Löschen" danger onClick={() => { setShowMenu(false); onDelete(); }} />
        </div>
      )}

      {/* Click-away for context menu */}
      {showMenu && <div style={{ position: 'fixed', inset: 0, zIndex: 4 }} onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />}
    </div>
  );
}

// Context menu button
function MenuButton({ icon, label, onClick, danger = false }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
        danger
          ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12 }}
    >
      {icon}
      {label}
    </button>
  );
}

// SVG icons for context menu
const openIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>;
const copyIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>;
const renameIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const deleteIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>;
